import type { MaskInputOptions, MaskTokens } from 'maska';

const DEFAULT_SLOT_CHARS = new Set(['#', '0', '9', 'A', 'a', 'S', 's', '@', '*', 'L', 'l', 'C', 'c']);

const DEFAULT_SLOT_TOKENS: MaskTokens = {
  'L': { pattern: /\p{L}/u },
  'l': { pattern: /\p{L}/u, optional: true },
  'A': { pattern: /[\p{L}0-9]/u },
  'a': { pattern: /[\p{L}0-9]/u, optional: true },
  'C': { pattern: /./ },
  'c': { pattern: /./, optional: true },
  '0': { pattern: /[0-9]/ },
  '9': { pattern: /[0-9]/, optional: true },
  '#': { pattern: /[0-9+-]/, optional: true },
  'S': { pattern: /./ },
  's': { pattern: /./, optional: true },
  '@': { pattern: /./ },
  '*': { pattern: /./ },
};

type MaskSegment = { type: 'literal'; text: string } | { type: 'group'; size: number };

type SlotCell = { groupIndex: number; valueIndex: number; char: string };

type MaskValueState = {
  sizes: number[];
  cells: SlotCell[];
  flags: boolean[];
};

function getMaskOptions(mask: MaskInputOptions | string): MaskInputOptions {
  return typeof mask === 'string' ? { mask } : mask;
}

function getMaskPattern(mask: MaskInputOptions | string) {
  const options = getMaskOptions(mask);
  return typeof options.mask === 'string' ? options.mask : '';
}

function getMaskTokens(mask: MaskInputOptions | string): MaskTokens {
  return getMaskOptions(mask).tokens ?? {};
}

function hasExplicitTokens(mask: MaskInputOptions | string) {
  return typeof mask !== 'string' && mask.tokens != null;
}

function isSlotSymbol(char: string, tokens: MaskTokens, explicitTokens: boolean) {
  if (tokens[char]) {
    return true;
  }

  return !explicitTokens && DEFAULT_SLOT_CHARS.has(char);
}

function getSlotToken(char: string, tokens: MaskTokens, explicitTokens: boolean) {
  if (tokens[char]) {
    return tokens[char];
  }

  return explicitTokens ? undefined : DEFAULT_SLOT_TOKENS[char];
}

function getSlotTokens(mask: MaskInputOptions | string) {
  const pattern = getMaskPattern(mask);
  const tokens = getMaskTokens(mask);
  const explicitTokens = hasExplicitTokens(mask);
  const slotTokens: NonNullable<ReturnType<typeof getSlotToken>>[] = [];

  for (let index = 0; index < pattern.length; index++) {
    const char = pattern[index];

    if (char === '!' && index + 1 < pattern.length) {
      index++;
      continue;
    }

    const token = getSlotToken(char, tokens, explicitTokens);

    if (token) {
      slotTokens.push(token);
    }
  }

  return slotTokens;
}

function consumeInputChars(mask: MaskInputOptions | string, text: string, fromSlotIndex: number, maxCount: number) {
  const slotTokens = getSlotTokens(mask);
  const chars: string[] = [];
  const source = [...text];
  let index = 0;

  while (index < source.length && chars.length < maxCount) {
    const token = slotTokens[fromSlotIndex + chars.length];
    const char = source[index++];

    if (!token?.pattern || !token.pattern.test(char)) {
      continue;
    }

    chars.push(token.transform ? token.transform(char) : char);
  }

  return {
    accepted: chars.join(''),
    rest: source.slice(index).join(''),
  };
}

function acceptInputChars(mask: MaskInputOptions | string, text: string, fromSlotIndex: number, maxCount: number) {
  return consumeInputChars(mask, text, fromSlotIndex, maxCount).accepted;
}

function unwrapMaskedLiteralGroups(mask: MaskInputOptions | string, text: string, fromGroupIndex = 0) {
  const segments = getMaskSegments(mask);
  const sizes = segments
    .filter((segment): segment is { type: 'group'; size: number } => segment.type === 'group')
    .map((segment) => segment.size);
  const groups = sizes.slice(fromGroupIndex).map(() => '');
  let index = 0;
  let groupCursor = -1;
  let started = false;

  for (let segmentIndex = 0; segmentIndex < segments.length; segmentIndex++) {
    const segment = segments[segmentIndex];

    if (segment.type === 'literal') {
      if ((started || groupCursor + 1 >= fromGroupIndex) && text.startsWith(segment.text, index)) {
        index += segment.text.length;
      }

      continue;
    }

    groupCursor++;

    if (groupCursor < fromGroupIndex) {
      continue;
    }

    started = true;

    const resultIndex = groupCursor - fromGroupIndex;
    const literal = nextLiteralText(segments, segmentIndex);
    const stopOnLiteral = Boolean(literal) && isSeparatorLiteral(segments, segmentIndex);
    let taken = 0;
    let content = '';

    while (taken < segment.size && index < text.length) {
      if (stopOnLiteral && text.startsWith(literal, index)) {
        break;
      }

      const unexpectedLiteral = segments.find(
        (candidate, candidateIndex) =>
          candidate.type === 'literal' &&
          candidateIndex > 0 &&
          text.startsWith(candidate.text, index) &&
          !(stopOnLiteral && candidate.text === literal) &&
          isSeparatorLiteralAt(segments, candidateIndex)
      );

      if (unexpectedLiteral?.type === 'literal') {
        index += unexpectedLiteral.text.length;
        continue;
      }

      content += text[index];
      index++;
      taken++;
    }

    groups[resultIndex] = content;
  }

  while (index < text.length) {
    const matchedLiteral = segments.find(
      (segment) => segment.type === 'literal' && text.startsWith(segment.text, index)
    );

    if (!matchedLiteral || matchedLiteral.type !== 'literal') {
      break;
    }

    index += matchedLiteral.text.length;
  }

  return {
    groups,
    rest: index < text.length ? text.slice(index) : '',
  };
}

function getMaskSegments(mask: MaskInputOptions | string): MaskSegment[] {
  const pattern = getMaskPattern(mask);
  const tokens = getMaskTokens(mask);
  const explicitTokens = hasExplicitTokens(mask);
  const segments: MaskSegment[] = [];
  let literal = '';
  let groupSize = 0;

  const flushLiteral = () => {
    if (literal) {
      segments.push({ type: 'literal', text: literal });
      literal = '';
    }
  };

  const flushGroup = () => {
    if (groupSize) {
      segments.push({ type: 'group', size: groupSize });
      groupSize = 0;
    }
  };

  for (let index = 0; index < pattern.length; index++) {
    const char = pattern[index];

    if (char === '!' && index + 1 < pattern.length) {
      flushGroup();
      literal += pattern[index + 1];
      index++;
      continue;
    }

    if (isSlotSymbol(char, tokens, explicitTokens)) {
      flushLiteral();
      groupSize++;
      continue;
    }

    flushGroup();
    literal += char;
  }

  flushGroup();
  flushLiteral();

  return segments;
}

function nextLiteralText(segments: MaskSegment[], fromIndex: number) {
  return segments.slice(fromIndex + 1).find((segment) => segment.type === 'literal')?.text ?? '';
}

function isSeparatorLiteral(segments: MaskSegment[], groupSegmentIndex: number) {
  const literalIndex = segments.findIndex((segment, index) => index > groupSegmentIndex && segment.type === 'literal');

  if (literalIndex < 0) {
    return false;
  }

  return segments.slice(literalIndex + 1).some((segment) => segment.type === 'group');
}

function isSeparatorLiteralAt(segments: MaskSegment[], literalIndex: number) {
  if (segments[literalIndex]?.type !== 'literal') {
    return false;
  }

  return segments.slice(literalIndex + 1).some((segment) => segment.type === 'group');
}

function readMaskValue(mask: MaskInputOptions | string, value: string): MaskValueState {
  const segments = getMaskSegments(mask);
  const sizes = segments
    .filter((segment): segment is { type: 'group'; size: number } => segment.type === 'group')
    .map((segment) => segment.size);
  const cells: SlotCell[] = [];
  const flags = Array.from({ length: value.length }, () => false);
  let valueIndex = 0;
  let groupIndex = 0;

  for (let segmentIndex = 0; segmentIndex < segments.length; segmentIndex++) {
    const segment = segments[segmentIndex];

    if (segment.type === 'literal') {
      if (value.startsWith(segment.text, valueIndex)) {
        valueIndex += segment.text.length;
      }

      continue;
    }

    const literal = nextLiteralText(segments, segmentIndex);
    const stopOnLiteral = Boolean(literal) && isSeparatorLiteral(segments, segmentIndex);
    let taken = 0;

    while (taken < segment.size && valueIndex < value.length) {
      if (stopOnLiteral && value.startsWith(literal, valueIndex)) {
        break;
      }

      flags[valueIndex] = true;
      cells.push({ groupIndex, valueIndex, char: value[valueIndex] });
      valueIndex++;
      taken++;
    }

    groupIndex++;
  }

  return { sizes, cells, flags };
}

const MASK_STRUCTURAL_BLANK = '\u00A0';

export function stripMaskStructuralBlanks(value: string) {
  return value.replaceAll(MASK_STRUCTURAL_BLANK, '');
}

function isContentCell(cell: SlotCell) {
  return cell.char !== MASK_STRUCTURAL_BLANK;
}

export function readPaddedMaskValue(mask: MaskInputOptions | string, text: string) {
  const groups: string[] = [];
  let index = 0;

  for (const segment of getMaskSegments(mask)) {
    if (segment.type === 'literal') {
      if (!text.startsWith(segment.text, index)) {
        return null;
      }

      index += segment.text.length;
      continue;
    }

    groups.push(text.slice(index, index + segment.size).trim());
    index += segment.size;
  }

  if (text.slice(index).trim()) {
    return null;
  }

  return buildMaskedValue(mask, groups);
}

function buildMaskedValue(mask: MaskInputOptions | string, groups: string[]) {
  if (!groups.some((group) => group.length > 0)) {
    return '';
  }

  const segments = getMaskSegments(mask);
  const sizes = segments
    .filter((segment): segment is { type: 'group'; size: number } => segment.type === 'group')
    .map((segment) => segment.size);
  const normalizedGroups = groups.map((group, index) => {
    const laterHas = groups.slice(index + 1).some((item) => item.length > 0);

    if (laterHas && group.length === 0) {
      return MASK_STRUCTURAL_BLANK.repeat(sizes[index] ?? 0);
    }

    return group;
  });
  let result = '';
  let groupIndex = 0;

  for (let segmentIndex = 0; segmentIndex < segments.length; segmentIndex++) {
    const segment = segments[segmentIndex];

    if (segment.type === 'group') {
      result += normalizedGroups[groupIndex] ?? '';
      groupIndex++;
      continue;
    }

    if (segmentIndex === 0) {
      result += segment.text;
      continue;
    }

    const previous = normalizedGroups[groupIndex - 1] ?? '';
    const previousSegment = segments[segmentIndex - 1];
    const previousFull = previousSegment.type === 'group' && previous.length >= previousSegment.size;
    const laterHas = normalizedGroups.slice(groupIndex).some((group) => group.length > 0);

    if (previousFull || (previous.length > 0 && laterHas)) {
      result += segment.text;
    }
  }

  return result;
}

function cellsToGroups(cells: SlotCell[], sizes: number[]) {
  const groups = sizes.map(() => '');

  for (const cell of cells) {
    if (!isContentCell(cell)) {
      continue;
    }

    if (groups[cell.groupIndex].length < sizes[cell.groupIndex]) {
      groups[cell.groupIndex] += cell.char;
    }
  }

  return groups;
}

function countGroupContent(cells: SlotCell[], groupIndex: number, until = cells.length) {
  return cells.slice(0, until).filter((cell) => cell.groupIndex === groupIndex && isContentCell(cell)).length;
}

function resolveInsertGroupIndex(cells: SlotCell[], from: number, sizes: number[]) {
  if (from > 0 && from <= cells.length) {
    const previousGroup = cells[from - 1].groupIndex;

    if (countGroupContent(cells, previousGroup) < sizes[previousGroup]) {
      return previousGroup;
    }
  }

  if (from < cells.length) {
    return cells[from].groupIndex;
  }

  if (from > 0 && sizes.length) {
    return Math.min(cells[from - 1].groupIndex + 1, sizes.length - 1);
  }

  return 0;
}

function countSlotsBefore(flags: boolean[], position: number) {
  let count = 0;

  for (let index = 0; index < position && index < flags.length; index++) {
    if (flags[index]) {
      count++;
    }
  }

  return count;
}

function caretAfterSlotCount(flags: boolean[], slotCount: number) {
  if (slotCount <= 0) {
    return 0;
  }

  let seen = 0;
  let afterSlot = -1;

  for (let index = 0; index < flags.length; index++) {
    if (!flags[index]) {
      continue;
    }

    seen++;

    if (seen === slotCount) {
      afterSlot = index + 1;
      break;
    }
  }

  if (afterSlot < 0) {
    return flags.length;
  }

  let caret = afterSlot;

  while (caret < flags.length && !flags[caret]) {
    caret++;
  }

  return caret;
}

function caretForGroupOffset(mask: MaskInputOptions | string, value: string, groupIndex: number, localOffset: number) {
  const { cells, flags } = readMaskValue(mask, value);
  const groupCells = cells.filter((cell) => cell.groupIndex === groupIndex);
  const slotsBeforeGroup = cells.filter((cell) => cell.groupIndex < groupIndex).length;

  if (localOffset <= 0) {
    if (groupCells.length) {
      return groupCells[0].valueIndex;
    }

    if (!flags.some((flag) => flag)) {
      return flags.length;
    }

    return caretAfterSlotCount(flags, slotsBeforeGroup);
  }

  if (localOffset <= groupCells.length) {
    return groupCells[localOffset - 1].valueIndex + 1;
  }

  return caretAfterSlotCount(flags, slotsBeforeGroup + groupCells.length);
}

function caretAfterContentOffset(mask: MaskInputOptions | string, value: string, contentOffset: number) {
  const { cells, flags } = readMaskValue(mask, value);
  const contentCells = cells.filter(isContentCell);

  if (contentOffset <= 0) {
    if (contentCells.length) {
      return contentCells[0].valueIndex;
    }

    if (!flags.some((flag) => flag)) {
      return flags.length;
    }

    return 0;
  }

  if (contentOffset <= contentCells.length) {
    return contentCells[contentOffset - 1].valueIndex + 1;
  }

  if (contentCells.length) {
    return contentCells[contentCells.length - 1].valueIndex + 1;
  }

  return flags.length;
}

export function getMaskSlotFlags(mask: MaskInputOptions | string, value: string) {
  return readMaskValue(mask, value).flags;
}

export function getEditableCaretPositions(flags: boolean[]) {
  if (!flags.length) {
    return [0];
  }

  const positions = new Set<number>();

  for (let index = 0; index < flags.length; index++) {
    if (!flags[index]) {
      continue;
    }

    positions.add(index);
    positions.add(index + 1);
  }

  positions.add(flags.length);

  return [...positions].sort((left, right) => left - right);
}

export function snapCaretPosition(position: number, editablePositions: number[]) {
  if (!editablePositions.length) {
    return 0;
  }

  if (editablePositions.includes(position)) {
    return position;
  }

  let previous = editablePositions[0];
  let next = editablePositions[editablePositions.length - 1];

  for (const candidate of editablePositions) {
    if (candidate <= position) {
      previous = candidate;
    }

    if (candidate >= position) {
      next = candidate;
      break;
    }
  }

  return position - previous <= next - position ? previous : next;
}

export function moveCaretPosition(position: number, direction: 1 | -1, editablePositions: number[]) {
  if (!editablePositions.length) {
    return 0;
  }

  if (direction > 0) {
    return editablePositions.find((candidate) => candidate > position) ?? editablePositions.at(-1)!;
  }

  return [...editablePositions].reverse().find((candidate) => candidate < position) ?? editablePositions[0];
}

let measureCanvas: HTMLCanvasElement | null = null;

function measureInputTextWidth(input: HTMLInputElement, text: string) {
  measureCanvas ??= document.createElement('canvas');
  const context = measureCanvas.getContext('2d');

  if (!context) {
    return 0;
  }

  const style = getComputedStyle(input);
  context.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;

  return context.measureText(text).width;
}

export function getCaretIndexFromClientX(input: HTMLInputElement, clientX: number) {
  const style = getComputedStyle(input);
  const rect = input.getBoundingClientRect();
  const paddingLeft = Number.parseFloat(style.paddingLeft) || 0;
  const paddingRight = Number.parseFloat(style.paddingRight) || 0;
  const borderLeft = Number.parseFloat(style.borderLeftWidth) || 0;
  const value = input.value;
  const textWidth = measureInputTextWidth(input, value);
  const contentWidth = input.clientWidth - paddingLeft - paddingRight;
  const alignOffset = style.textAlign === 'center' ? Math.max(0, (contentWidth - textWidth) / 2) : 0;
  const x = clientX - rect.left - borderLeft - paddingLeft - alignOffset + input.scrollLeft;

  if (x <= 0) {
    return 0;
  }

  let low = 0;
  let high = value.length;

  while (low < high) {
    const mid = Math.ceil((low + high) / 2);

    if (measureInputTextWidth(input, value.slice(0, mid)) <= x) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }

  if (low < value.length) {
    const left = measureInputTextWidth(input, value.slice(0, low));
    const right = measureInputTextWidth(input, value.slice(0, low + 1));

    if (x - left > right - x) {
      return low + 1;
    }
  }

  return low;
}

function distributeInsertedChars(
  nextGroups: string[],
  sizes: number[],
  insertGroupIndex: number,
  prefix: string,
  groupTail: string,
  nextChars: string,
  laterGroups: string[]
) {
  const insertChars = [...nextChars];
  const capacity = Math.max((sizes[insertGroupIndex] ?? 0) - prefix.length, 0);

  nextGroups[insertGroupIndex] = prefix + [...(nextChars + groupTail)].slice(0, capacity).join('');

  const overflow = insertChars.slice(Math.min(insertChars.length, capacity));

  if (!overflow.length) {
    return insertChars.length;
  }

  const laterChars = [...(overflow.join('') + laterGroups.join(''))];
  let offset = 0;

  for (let groupIndex = insertGroupIndex + 1; groupIndex < sizes.length; groupIndex++) {
    const chunk = laterChars.slice(offset, offset + sizes[groupIndex]);
    nextGroups[groupIndex] = chunk.join('');
    offset += chunk.length;

    if (offset >= laterChars.length) {
      for (let clearIndex = groupIndex + 1; clearIndex < sizes.length; clearIndex++) {
        nextGroups[clearIndex] = '';
      }

      break;
    }
  }

  return insertChars.length;
}

export function replaceMaskedSlots(
  mask: MaskInputOptions | string,
  value: string,
  selectionStart: number,
  selectionEnd: number,
  replacement: string
) {
  const { sizes, cells, flags } = readMaskValue(mask, value);
  const from = countSlotsBefore(flags, selectionStart);
  const to = countSlotsBefore(flags, selectionEnd);

  const insertGroupIndex = resolveInsertGroupIndex(cells, from, sizes);
  const keptBefore = countGroupContent(cells, insertGroupIndex, from);
  const groups = cellsToGroups([...cells.slice(0, from), ...cells.slice(to)], sizes);
  const prefix = (groups[insertGroupIndex] ?? '').slice(0, keptBefore);
  const groupTail = (groups[insertGroupIndex] ?? '').slice(keptBefore);
  const isPaste = [...replacement].length > 1;
  const nextGroups = groups.map((group, index) => {
    if (index < insertGroupIndex) {
      return group;
    }

    if (index === insertGroupIndex) {
      return prefix;
    }

    return isPaste ? '' : group;
  });

  let insertCount = 0;

  if (isPaste) {
    const { groups: pieces, rest } = unwrapMaskedLiteralGroups(mask, replacement, insertGroupIndex);
    const hasHole = pieces.some((piece, index) => piece.length === 0 && index < pieces.length - 1);

    if (hasHole) {
      const slotsBeforeGroup = sizes.slice(0, insertGroupIndex).reduce((sum, size) => sum + size, 0);
      const available = sizes.slice(insertGroupIndex).reduce((sum, size) => sum + size, 0) - keptBefore;
      const nextChars = acceptInputChars(mask, pieces.join('') + rest, slotsBeforeGroup + keptBefore, available);

      insertCount = distributeInsertedChars(
        nextGroups,
        sizes,
        insertGroupIndex,
        prefix,
        groupTail,
        nextChars,
        groups.slice(insertGroupIndex + 1)
      );
    } else {
      let carry = '';
      let tail = rest + groups.slice(insertGroupIndex + 1).join('');

      for (let offset = 0; offset < sizes.length - insertGroupIndex; offset++) {
        const groupIndex = insertGroupIndex + offset;
        const usedPrefix = offset === 0 ? prefix.length : 0;
        const room = sizes[groupIndex] - usedPrefix;
        const slotStart = sizes.slice(0, groupIndex).reduce((sum, size) => sum + size, 0) + usedPrefix;
        let incoming = carry + (pieces[offset] ?? '');

        if (offset >= pieces.length - 1 && tail) {
          incoming += tail;
          tail = '';
        }

        const { accepted, rest: nextRest } = consumeInputChars(mask, incoming, slotStart, room);

        carry = nextRest;
        insertCount += [...accepted].length;

        if (offset === 0) {
          nextGroups[groupIndex] = prefix + [...(accepted + groupTail)].slice(0, room).join('');
        } else {
          nextGroups[groupIndex] = accepted;
        }
      }
    }
  } else {
    const slotsBeforeGroup = sizes.slice(0, insertGroupIndex).reduce((sum, size) => sum + size, 0);
    const available = sizes.slice(insertGroupIndex).reduce((sum, size) => sum + size, 0) - keptBefore;
    const nextChars = acceptInputChars(mask, replacement, slotsBeforeGroup + keptBefore, available);

    insertCount = distributeInsertedChars(
      nextGroups,
      sizes,
      insertGroupIndex,
      prefix,
      groupTail,
      nextChars,
      groups.slice(insertGroupIndex + 1)
    );
  }

  const nextValue = buildMaskedValue(mask, nextGroups);
  const contentBefore = cells.slice(0, from).filter(isContentCell).length;

  return {
    value: nextValue,
    caret: caretAfterContentOffset(mask, nextValue, contentBefore + insertCount),
  };
}

export function deleteMaskedSlots(
  mask: MaskInputOptions | string,
  value: string,
  selectionStart: number,
  selectionEnd: number,
  direction: 'backward' | 'forward'
) {
  const { sizes, cells, flags } = readMaskValue(mask, value);
  const positions = getEditableCaretPositions(flags);
  let from = countSlotsBefore(flags, selectionStart);
  let to = countSlotsBefore(flags, selectionEnd);

  if (selectionStart === selectionEnd) {
    if (direction === 'backward') {
      if (selectionStart === 0) {
        return { value, caret: 0 };
      }

      if (!flags[selectionStart - 1]) {
        return { value, caret: moveCaretPosition(selectionStart, -1, positions) };
      }

      from -= 1;
      to = from + 1;
    } else if (selectionStart >= flags.length) {
      return { value, caret: selectionStart };
    } else if (!flags[selectionStart]) {
      return { value, caret: moveCaretPosition(selectionStart, 1, positions) };
    } else {
      to = from + 1;
    }
  }

  if (from >= to || from >= cells.length) {
    return { value, caret: selectionStart };
  }

  if (cells.slice(from, to).every((cell) => !isContentCell(cell))) {
    if (selectionStart !== selectionEnd) {
      return { value, caret: selectionStart };
    }

    if (direction === 'forward') {
      const nextContent = cells.findIndex((cell, index) => index >= to && isContentCell(cell));

      if (nextContent < 0) {
        return { value, caret: moveCaretPosition(selectionStart, 1, positions) };
      }

      from = nextContent;
      to = nextContent + 1;
    } else {
      let previousContent = -1;

      for (let index = from - 1; index >= 0; index--) {
        if (isContentCell(cells[index])) {
          previousContent = index;
          break;
        }
      }

      if (previousContent < 0) {
        return { value, caret: moveCaretPosition(selectionStart, -1, positions) };
      }

      from = previousContent;
      to = previousContent + 1;
    }
  }

  const targetGroupIndex = cells[from].groupIndex;
  const localOffset = countGroupContent(cells, targetGroupIndex, from);
  const nextValue = buildMaskedValue(mask, cellsToGroups([...cells.slice(0, from), ...cells.slice(to)], sizes));

  return {
    value: nextValue,
    caret: caretForGroupOffset(mask, nextValue, targetGroupIndex, localOffset),
  };
}
